import {isCrossProjectRun} from '../../components/RunSelector/util';
import {type PinnedRunsProjectLookupQuery} from '../../generated/graphql';
import {type ApolloClient} from '../../state/types';
import * as Filter from '../filters';
import {
  emptyStoredFilter,
  FILTER_FORMAT_V2,
  type FilterV2Root,
} from '../filterV2';
import {
  emptyReportRunSetSelectAll,
  generateRunsetID,
  type RunSetConfig,
} from '../section';
import {PINNED_RUNS_PROJECT_LOOKUP} from './crossProjectPinsRunSets.query';

type RunWithProjectInfo = {
  runId: string;
  runName: string;
  project?: {
    internalId: string;
    entityName: string;
    name: string;
  };
};

/**
 * Builds run set configs for each distinct foreign project if
 * pinned/baseline runs contain cross-project runs.
 * Used for exporting workspace panels to reports.
 */
export async function buildCrossProjectPinsRunSets(
  client: ApolloClient,
  sourceRunSets: readonly RunSetConfig[],
  workspaceProject: {entityName: string; name: string}
): Promise<RunSetConfig[]> {
  const pinnedRunIds = getPinnedRunIds(sourceRunSets);
  if (!pinnedRunIds.length) {
    return [];
  }

  try {
    const runs = await getRunsWithProjectInfo(client, pinnedRunIds);
    return runSetsForForeignProjects(runs, workspaceProject);
  } catch (err) {
    console.error('Failed to load pinned run projects for report export', err);
    return [];
  }
}

function getPinnedRunIds(runSets: readonly RunSetConfig[]): string[] {
  const ids = new Set<string>();
  for (const rs of runSets) {
    rs.pinnedRunIds?.forEach(id => ids.add(id));
    if (rs.baselineRunId != null) {
      ids.add(rs.baselineRunId);
    }
  }
  return [...ids];
}

async function getRunsWithProjectInfo(
  client: ApolloClient,
  pinnedRunIds: readonly string[]
): Promise<RunWithProjectInfo[]> {
  const {data} = await client.query<PinnedRunsProjectLookupQuery>({
    query: PINNED_RUNS_PROJECT_LOOKUP,
    variables: {pinnedRunIds, order: '-createdAt'},
    fetchPolicy: 'network-only',
  });
  const runs = data?.pinnedRuns?.runs ?? [];
  return runs.map(run => ({
    runId: run.id,
    runName: run.name,
    project: run.project ?? undefined,
  }));
}

function runSetsForForeignProjects(
  runs: readonly RunWithProjectInfo[],
  workspaceProject: {entityName: string; name: string}
): RunSetConfig[] {
  // filter to foreign runs only
  const foreignRuns = runs.filter((r): r is Required<RunWithProjectInfo> =>
    isCrossProjectRun(r.project, workspaceProject)
  );
  if (foreignRuns.length === 0) {
    return [];
  }

  // group runs by project
  const runsByProjects = new Map<string, Required<RunWithProjectInfo>[]>();
  for (const run of foreignRuns) {
    const key = `${run.project.entityName}/${run.project.name}`;
    const group = runsByProjects.get(key);
    if (group) {
      group.push(run);
    } else {
      runsByProjects.set(key, [run]);
    }
  }

  // create a run set for each project
  return [...runsByProjects.values()].map(runs => {
    const {project} = runs[0];
    const runNames = runs.map(r => r.runName);
    return {
      ...emptyReportRunSetSelectAll(),
      id: generateRunsetID(),
      name: `Pinned runs (${project.entityName}/${project.name})`,
      project: {
        id: project.internalId,
        entityName: project.entityName,
        name: project.name,
      },
      filters: runNamesFilters(runNames),
    };
  });
}

function runNamesFilters(runNames: string[]): FilterV2Root {
  if (runNames.length === 0) {
    return emptyStoredFilter();
  }
  return {
    filterFormat: FILTER_FORMAT_V2,
    filters: [Filter.runNamesFilter(runNames)],
  };
}
